/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 6th 2022, 4:29:13 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Chip from "@mui/material/Chip";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { EventDirection, EventType, NODE_TYPES, TaskTemplate } from "lib/schemas";
import { BPMNTaskTypeIcon, BPMNEventIcon } from "service-builder/components/workflow-builder/TaskTemplateIcons";
import { MergeIcon, SplitIcon, EndIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { NODE_TYPE_WIDGETS } from "./TaskTypeWidgets";

interface TaskTemplateListItemProps {
  selected: boolean;
  onClick: Function;
  taskTemplate?: TaskTemplate;
  handleListItemEdit: (editedTaskTemplate: TaskTemplate) => void;
}

export const EventNodeWidget = ({
  id,
  title,
  isInterrupting,
  eventDirection,
  eventType,
  size,
  showLabel = true,
  showTooltip = true,
}: {
  id: string;
  title: string;
  isInterrupting: boolean;
  eventDirection: keyof typeof EventDirection;
  eventType: keyof typeof EventType;
  size: number;
  showLabel?: boolean;
  showTooltip?: boolean;
}) => (
  <Tooltip key={id} title={showTooltip ? <Typography>{title}</Typography> : ""} placement="right" arrow>
    <div className="os-event-node-container">
      <BPMNEventIcon
        eventDirection={eventDirection}
        eventType={eventType}
        isInterrupting={isInterrupting}
        width={size}
        cursor="pointer"
      />
      {showLabel ? <Typography>{title}</Typography> : null}
    </div>
  </Tooltip>
);

export const TaskTemplateListItemDefaultWidget = ({
  selected,
  onClick,
  taskTemplate,
  handleListItemEdit,
}: TaskTemplateListItemProps) => {
  const handleOnClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick(event);
  };

  // eslint-disable-next-line
  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    handleListItemEdit({ ...taskTemplate, title: event.target.value });
  };

  return (
    <div className={`os-task-template-list-item-container ${taskTemplate.enabled ? "" : "disabled"}`}>
      <TaskTemplateListItemDefaultWidgetLayout
        type={taskTemplate.type}
        selected={selected}
        handleOnClick={taskTemplate.enabled ? handleOnClick : null}
        primary={
          // <TextEditToggler value={taskTemplate.title} onBlur={handleOnBlur} initialEditMode={selected} />
          taskTemplate.title
        }
      />
    </div>
  );
};

interface TaskTemplateListItemLayoutProps {
  selected: boolean;
  handleOnClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  primary: React.ReactNode;
  type: keyof typeof NODE_TYPES;
}

export const TaskTemplateListItemDefaultWidgetLayout = ({
  selected,
  handleOnClick,
  primary,
  type,
}: TaskTemplateListItemLayoutProps) => {
  return (
    /* Disabling Material UI ripple effect here because it adversely affects the HTML5 default drag preview by showing
          the wrong preview. https://github.com/react-dnd/react-dnd/issues/832 */
    <ListItemButton
      selected={selected}
      onClick={handleOnClick}
      disableRipple
      className={NODE_TYPE_WIDGETS[type]?.nodeTypeCssClassName}
    >
      <ListItemText
        className="os-task-template-list-item-content"
        primary={primary}
        secondary={<TaskTemplateAvatar type={type} />}
      />
    </ListItemButton>
  );
};

const TaskTemplateAvatar = ({ type }: { type: keyof typeof NODE_TYPES }) => (
  <Stack direction="row" spacing={1}>
    <Chip
      icon={<BPMNTaskTypeIcon taskType={type} color={COLORSCHEME.primary} />}
      label={NODE_TYPES[type]}
      size="small"
    />
  </Stack>
);

export const TaskTemplateChartMergeNodeWidget = ({ selected, onClick, taskTemplate }: TaskTemplateListItemProps) => (
  <div className="os-workflow-chart-view-gateway-node" style={{ background: `${COLORSCHEME.warning}A0` }}>
    <ListItemButton selected={selected} onClick={(event) => onClick(event)} disableRipple>
      <ListItemText
        className="os-task-template-list-item-content"
        primary={<MergeIcon width={30} height={20} style={{ transform: "rotate(180deg)" }} />}
        secondary={<Typography>{taskTemplate.title}</Typography>}
      />
    </ListItemButton>
  </div>
);

export const TaskTemplateChartSplitNodeWidget = ({ selected, onClick, taskTemplate }: TaskTemplateListItemProps) => (
  <div className="os-workflow-chart-view-gateway-node" style={{ background: `${COLORSCHEME.info}A0` }}>
    <ListItemButton selected={selected} onClick={(event) => onClick(event)} disableRipple>
      <ListItemText
        className="os-task-template-list-item-content"
        primary={<SplitIcon width={30} height={20} style={{ transform: "rotate(180deg)" }} />}
        secondary={<Typography>{taskTemplate.title}</Typography>}
      />
    </ListItemButton>
  </div>
);

export const TaskTemplateChartEndNodeWidget = ({ selected, onClick, taskTemplate }: TaskTemplateListItemProps) => {
  const style: { [key in "primary" | "secondary"]: React.CSSProperties } = {
    primary: {},
    secondary: { paddingLeft: 2 },
  };
  return (
    <div className="os-workflow-chart-view-end-node" style={{ background: `${COLORSCHEME.secondary}70` }}>
      <ListItemButton selected={selected} onClick={(event) => onClick(event)} disableRipple>
        <ListItemText
          className="os-task-template-list-item-content"
          primary={<EndIcon width={30} height={30} />}
          secondary={<Typography sx={style.secondary}>{taskTemplate.title}</Typography>}
        />
      </ListItemButton>
    </div>
  );
};
